import { useState, useContext, useEffect } from "react";
import { FilterContext } from "../../Context/FilterContext";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Paper,
  Divider,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import "./FilterCard.css";
import { fetchStudyDesigns } from "../../Utils/functions";

export const FilterCard = ({
  toggleModal,
  setSearchMode,
  searchMode,
  availableSearchModes,
}) => {
  const { filters, setFilters } = useContext(FilterContext);
  const { fromDate, toDate, abstracts, studyDesigns } = filters;

  const [error, setError] = useState({ from: "", to: "" });
  const [studyDesignOptions, setStudyDesignOptions] = useState([]);
  const [loadingStudyDesigns, setLoadingStudyDesigns] = useState(false);

  useEffect(() => {
    const loadStudyDesigns = async () => {
      try {
        setLoadingStudyDesigns(true);
        const data = await fetchStudyDesigns();
        const transformedData = data.map((design) => ({
          value: design.value,
          label: design.label,
        }));
        setStudyDesignOptions(transformedData);
      } catch (error) {
        console.error("Error loading study designs", error);
      } finally {
        setLoadingStudyDesigns(false);
      }
    };
    loadStudyDesigns();
  }, []);

  const handleClearAll = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      abstracts: "",
      studyDesigns: [],
    });
    setError({ from: "", to: "" });
  };

  const validateDates = () => {
    let isValid = true;
    const errors = { from: "", to: "" };

    const DATE_REGEX = /^\d{4}\/\d{2}\/\d{2}$/;

    const parseDate = (value) => {
      if (!DATE_REGEX.test(value)) {
        return null;
      }
      const [yearStr, monthStr, dayStr] = value.split("/");
      const year = Number(yearStr);
      const month = Number(monthStr);
      const day = Number(dayStr);

      const date = new Date(year, month - 1, day);

      if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
      ) {
        return null;
      }

      return date;
    };

    const fromDateObj = fromDate ? parseDate(fromDate) : null;
    const toDateObj = toDate ? parseDate(toDate) : null;

    if (fromDate && !fromDateObj) {
      errors.from = "Enter a valid date in YYYY/MM/DD format";
      isValid = false;
    }

    if (toDate && !toDateObj) {
      errors.to = "Enter a valid date in YYYY/MM/DD format";
      isValid = false;
    }

    if (fromDateObj && toDateObj && fromDateObj > toDateObj) {
      errors.from = "From date cannot be after To date";
      isValid = false;
    }

    setError(errors);
    return isValid;
  };

  const handleStudyDesignChange = (value, checked) => {
    setFilters((prev) => ({
      ...prev,
      studyDesigns: checked
        ? [...prev.studyDesigns, value]
        : prev.studyDesigns.filter((id) => id !== value),
    }));
  };

  const handleApply = () => {
    if (validateDates()) {
      toggleModal();
    }
  };

  return (
    <Paper elevation={8} className="FilterCard">
      <Box className="FilterCard__Header">
        <Box className="FilterCard__Title">
          <FilterIcon className="FilterCard__Icon" />
          <Typography
            variant="h5"
            component="h2"
            className="FilterCard__TitleText"
          >
            Search Filters
          </Typography>
        </Box>
        <IconButton
          onClick={toggleModal}
          className="FilterCard__CloseButton"
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider className="FilterCard__Divider" />

      <Box className="FilterCard__Content">
        <Box className="YearRange">
          <Typography variant="h6" component="h3" className="YearRange__Title">
            Publication Date Range
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className="YearRange__Subtitle"
          >
            Specify the time period for your search
          </Typography>

          <Stack direction="row" spacing={2} className="YearRange__Inputs">
            <TextField
              label="From Date"
              placeholder="YYYY/MM/DD"
              variant="outlined"
              value={fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
              error={!!error.from}
              helperText={error.from}
              className="YearRange__Input"
              size="small"
            />
            <TextField
              label="To Date"
              placeholder="YYYY/MM/DD"
              variant="outlined"
              value={toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
              error={!!error.to}
              helperText={error.to}
              className="YearRange__Input"
              size="small"
            />
          </Stack>
        </Box>

        <Box className="SearchModeSection">
          <Typography
            variant="h6"
            component="h3"
            className="SearchModeSection__Title"
          >
            Search Mode
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className="SearchModeSection__Subtitle"
          >
            Choose how you want to search through the data
          </Typography>

          <FormControl
            size="small"
            variant="outlined"
            className="SearchModeSection__Dropdown"
            fullWidth
          >
            <Select
              labelId="search-mode-label"
              id="search-mode-select"
              value={searchMode}
              onChange={(e) => setSearchMode(e.target.value)}
              displayEmpty
            >
              {availableSearchModes.map((mode) => (
                <MenuItem key={mode} value={mode}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="Methodology">
          <Typography
            variant="h6"
            component="h3"
            className="Methodology__Title"
          >
            {" "}
            Methodology{" "}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className="Methodology__Subtitle"
          >
            Select study design
          </Typography>

          {loadingStudyDesigns ? (
            <Typography variant="body2" color="text.secondary">
              Loading study designs...
            </Typography>
          ) : (
            <FormGroup className="Methodology__CheckboxGroup">
              {studyDesignOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={studyDesigns.includes(option.value)}
                      onChange={(e) =>
                        handleStudyDesignChange(option.value, e.target.checked)
                      }
                      value={option.value}
                      size="small"
                    />
                  }
                  label={option.label}
                  className="Methodology__Checkbox"
                />
              ))}
            </FormGroup>
          )}
        </Box>
      </Box>

      <Divider className="FilterCard__Divider" />

      <Box className="FilterCard__Actions">
        <Button
          variant="outlined"
          onClick={handleClearAll}
          className="FilterCard__ClearButton"
          size="medium"
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          className="FilterCard__ApplyButton"
          size="medium"
        >
          Apply Filters
        </Button>
      </Box>
    </Paper>
  );
};
